import { Box, FormControlLabel, Radio, TextField } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ApplicationPermissionType, ConfirmationDialogResult, ReportSharingType } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { IReportItem, ReportItem } from "source/models/report/reportItem";
import { ISavedReport, SavedReport } from "source/models/report/savedReport";
import * as reportService from "source/models/services/reportService";
import { ISessionState } from "source/models/session/sessionState";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { FormField } from "ui/Scripts/source/components/framework/FormField";

//SaveReportDialog

export interface ISaveReportFilters {
    generalFilter: any;
    reportFilter: any;
    showInFrontPage: boolean;
}

export interface ISaveReportDialogProp {
    sessionState: ISessionState;
    classes?: string;
    companyId: string;
    applicationPermissionType: ApplicationPermissionType;
    report: IReportItem;
    generalFilter: any;
    reservedNames: string[];
    onGetReportSaveFilters?: () => ISaveReportFilters;
    onOk: (savedReport: SavedReport, reportSharingType: ReportSharingType) => void;
    onCancel: () => void;
}

export interface ISaveReportDialogState {
    name: string;
    reportSharingType: ReportSharingType;
}

export class SaveReportDialog extends React.Component<ISaveReportDialogProp, ISaveReportDialogState> {
    private orgStateHash: string = "";

    constructor(props: ISaveReportDialogProp) {
        super(props);
        this.state = {
            name: props.report.getTitle(),
            reportSharingType: props.sessionState.isSuperAdmin ? props.report.reportSharingType : ReportSharingType.NotShared
        };
        this.orgStateHash = this.getHashFromState(props, this.state);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "name") {
            this.setState({ name: value });
        }
    };

    changeSharingType = (reportSharingType: ReportSharingType) => {
        this.setState({ reportSharingType });
    };
    
    static getReportFilterFromTaskType = (taskType: ITaskEditTypeItem, taskDataRow: ITaskDataRow): any => {
        let result = null;
        if (taskType && taskDataRow) {
            const rows = TaskDataRow.getDynamicDataRowsFromJsonTaskDataRows([taskDataRow], taskType.formCols);
            if (rows.length > 0) {
                result = rows[0];
            }
        }
        return result;
    };

    static getReportFilter = (report: IReportItem): any => {
        return this.getReportFilterFromTaskType(report.taskType, report.taskDataRow);
    };

    static getSavedReport = (report: IReportItem, name: string, generalFilter: any, reportFilter: any, showInFrontPage: boolean, applicationPermissionType: number): SavedReport => {
        const result = new SavedReport();
        result.id = name;
        result.title = name;
        result.reportId = report.reportId;
        result.applicationPermissionType = applicationPermissionType;
        result.generalFilter = generalFilter ?? {};
        result.reportFilter = reportFilter ?? {};
        result.sortColumn = report.sortColumn;
        result.sortOrderIsAsc = report.sortOrderIsAsc;
        result.showInFrontPage = !!showInFrontPage;
        return result;
    };

    getSavedReportFromState = (props: ISaveReportDialogProp, state: ISaveReportDialogState, beforeSave: boolean): SavedReport => {
        let generalFilter = props.generalFilter;
        let reportFilter = SaveReportDialog.getReportFilter(props.report);
        let showInFrontPage = props.report.showInFrontPage;
        if (beforeSave && props.onGetReportSaveFilters) {
            const filters = props.onGetReportSaveFilters();
            generalFilter = filters.generalFilter;
            reportFilter = filters.reportFilter;
            showInFrontPage = filters.showInFrontPage;
        }
        return SaveReportDialog.getSavedReport(props.report, state.name, generalFilter, reportFilter, showInFrontPage, props.applicationPermissionType);
    };

    static getSaveData = (companyId: string, orgReportId: string, reportSharingType: ReportSharingType, applicationPermissionType: number, savedReport: SavedReport): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", companyId);
        result.append("applicationPermissionType", applicationPermissionType);
        result.append("orgId", !Base.isNullOrUndefined(orgReportId) ? orgReportId : "");
        result.append("reportSharingType", reportSharingType.toString(10));
        result.append("item", savedReport);
        return result;
    };

    getSaveDataFromState = (props: ISaveReportDialogProp, state: ISaveReportDialogState, savedReport: SavedReport): SaveDataObj => {
        return SaveReportDialog.getSaveData(props.companyId, null, state.reportSharingType, props.applicationPermissionType, savedReport);
    };
    
    getHashFromState = (props: ISaveReportDialogProp, state: ISaveReportDialogState): string => {
        return this.getSaveDataFromState(props, state, this.getSavedReportFromState(props, state, false)).hash;
    };

    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.name) {
                AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                return resolve(false);
            }
            if (props.reservedNames.find(i => i.equalIgnoreCase(state.name))) {
                AppUtils.showErrorMessage(Translations.NameIsAlreadyInUse);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SavedReport> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const savedReport = this.getSavedReportFromState(props, state, true);
        const saveData = this.getSaveDataFromState(props, state, savedReport);
        //Call server
        const result = await AppUtils.callService(() => reportService.saveReport(saveData.obj));
        if (!result) return null;
        this.orgStateHash = saveData.hash;
        return savedReport;
    };

    handleSave = async () => {
        const props = this.props;
        const result = await this.saveToDb();
        if (!result) return;
        props.onOk(result, this.state.reportSharingType);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave();
        } else {
            props.onCancel();
        }
    };

    static saveReportToDb = async (report: IReportItem, generalFilter: any, companyId: string, applicationPermissionType: number): Promise<SavedReport> => {
        if (!report) return;
        const savedReport = SaveReportDialog.getSavedReport(report, report.getTitle(), generalFilter, SaveReportDialog.getReportFilter(report), report.showInFrontPage, applicationPermissionType);
        const saveData = SaveReportDialog.getSaveData(companyId, report.id, report.reportSharingType, applicationPermissionType, savedReport);
        const result = await AppUtils.callService(() => reportService.saveReport(saveData.obj));
        if (!result) return null;
        return savedReport;
    };

    static removeReportFromDb = async (userReports: IReportItem[], companyId: string, reportId: string, reportSharingType: ReportSharingType, applicationPermissionType: number): Promise<ApiSuccess> => {
        if (!reportId) return;
        const userReport = userReports.find(i => i.id === reportId);
        if (!userReport) return;
        const confirmationResult = await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveReportParameter, userReport.title));
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;
        const saveData = new SaveDataObj();
        saveData.append("companyId", companyId);
        saveData.append("applicationPermissionType", applicationPermissionType);
        saveData.append("id", reportId);
        saveData.append("reportSharingType", reportSharingType);
        return await AppUtils.callService(() => reportService.removeReport(saveData.obj), false);
    };

    static getReportStateChangeAfterReportSave = (state: { reports: IReportItem[], savedReports: IReportItem[] }, orgReportId: string, savedReport: ISavedReport, reportSharingType: ReportSharingType): {
        showReportSaveDialog: boolean,
        savedReports: IReportItem[],
        activeReportId: string,
        activeSavedReportId: string,
        reportFilterReport: IReportItem
    } => {
        if (!savedReport) return;
        const savedReports = state.savedReports.filter(i => i.id !== orgReportId || i.reportSharingType !== reportSharingType);
        const report = state.reports.find(i => i.id === savedReport.reportId);
        if (!report) return;
        const newReport = ReportItem.createReportItemFromSavedReport(report, savedReport, reportSharingType);
        savedReports.push(newReport);
        ReportItem.sortReportItems(savedReports);
        return {
            showReportSaveDialog: false,
            savedReports,
            activeReportId: "",
            activeSavedReportId: savedReport.id,
            reportFilterReport: newReport
        };
    };

    static getReportStateChangeAfterReportFilterChange = (state: { reports: IReportItem[], savedReports: IReportItem[], activeReportId: string, activeSavedReportId: string }, parameters: ITaskDataRow, showInFrontPage: boolean): {
        reports: IReportItem[],
        savedReports: IReportItem[],
        reportFilter: any
    } => {
        const reports = state.reports.slice(0);
        const savedReports = state.savedReports.slice(0);
        const index = state.activeReportId
            ? reports.findIndex(i => i.id === state.activeReportId)
            : savedReports.findIndex(i => i.id === state.activeSavedReportId);
        if (index < 0) return null;
        let reportFilter = null;
        if (state.activeReportId) {
            const report = new ReportItem(reports[index]);
            report.taskDataRow = parameters;
            report.showInFrontPage = showInFrontPage;
            reportFilter = SaveReportDialog.getReportFilter(report);
            reports[index] = report;
        } else {
            const savedReport = new ReportItem(savedReports[index]);
            savedReport.taskDataRow = parameters;
            savedReport.showInFrontPage = showInFrontPage;
            savedReport.reportFilter = SaveReportDialog.getReportFilter(savedReport);
            reportFilter = savedReport.reportFilter;
            savedReports[index] = savedReport;
        }
        return {
            reports,
            savedReports,
            reportFilter
        };
    };

    render() {
        const state = this.state;
        const props = this.props;
        return (
            <Dialog
                id="save-report-dialog"
                open
                maxWidth="sm"
                onClose={this.handleCancel}
                title={Translations.SaveReportAs}
                body={
                    <>
                        <Box sx={{ marginTop: "-1.5rem" }}>
                            <FormField title={Translations.Name} required fullWidth>
                                <TextField type="text" name="name" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.name} required={true} onChange={this.handleChange} size="small" fullWidth />
                            </FormField>
                        </Box>
                        {props.sessionState.isSuperAdmin &&
                            <FormField title="">
                                <FormControlLabel
                                    label={Translations.UserReport}
                                    control={
                                        <Radio
                                            checked={state.reportSharingType === ReportSharingType.NotShared}
                                            onClick={() => this.changeSharingType(ReportSharingType.NotShared)}
                                        />
                                    }
                                />
                                <FormControlLabel
                                    label={Translations.SharedReportCompany}
                                    control={
                                        <Radio
                                            checked={state.reportSharingType === ReportSharingType.Company}
                                            onClick={() => this.changeSharingType(ReportSharingType.Company)}
                                        />}
                                />
                                <FormControlLabel
                                    label={Translations.SharedReportAllCompanies}
                                    control={
                                        <Radio
                                            checked={state.reportSharingType === ReportSharingType.AllCompanies}
                                            onClick={() => this.changeSharingType(ReportSharingType.AllCompanies)}
                                        />
                                    }
                                />
                            </FormField>
                        }
                    </>
                }
                buttons={[
                    { title: Translations.Cancel, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.Save, variant: "confirmation", onClick: this.handleSave },
                ]}
            />
        );
    }
}